import React, { useEffect, useState } from "react";
import { Check, Info, MessageCircle, RefreshCw, RotateCcw, Save, Settings, X } from "lucide-react";
import { toast } from "react-toastify";

import { Action } from "../../../components/ui/Action";
import { Table } from "../../../components/ui/Table";
import { CrmPetController } from "../../../services/controllers/crmPet";
import {
  CRM_PET_RULES,
  hasCompleteCrmPetMessages,
  prepareCrmPetConfigPayload,
} from "../../../services/formatResponses/crmPet";
import { FAIXAS_DEFAULT } from "../../../store/crmPet/constants";
import { useCrmPet } from "../../../store/crmPet/useCrmPet";

import { VARIAVEIS } from "../constants";

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function updateRuleSubtype(config, ruleCode, subtypeCode, changes) {
  return {
    ...config,
    rules: config.rules.map((rule) => (
      rule.ruleCode === ruleCode
        ? {
          ...rule,
          subtypes: rule.subtypes.map((subtype) => (
            subtype.subtypeCode === subtypeCode ? { ...subtype, ...changes } : subtype
          )),
        }
        : rule
    )),
  };
}

export function ConfigModal() {
  const CCrmPet = CrmPetController();
  const showConfig = useCrmPet((state) => state.data.showConfig);
  const [config, setConfig] = useState({ faixas: [], rules: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!showConfig) return;
    setLoading(true);
    setSaved(false);
    setError(null);
    CCrmPet.findConfig()
      .then((result) => setConfig(clone(result)))
      .catch(() => setError("Erro ao carregar as configurações."))
      .finally(() => setLoading(false));
  }, [showConfig]);

  const updateFaixa = (index, mensagem) => {
    setConfig((current) => ({
      ...current,
      faixas: current.faixas.map((faixa, currentIndex) => (
        currentIndex === index ? { ...faixa, mensagem } : faixa
      )),
    }));
    setSaved(false);
  };

  const updateRule = (ruleCode, changes) => {
    setConfig((current) => ({
      ...current,
      rules: current.rules.map((rule) => (
        rule.ruleCode === ruleCode ? { ...rule, ...changes } : rule
      )),
    }));
    setSaved(false);
  };

  const updateSubtype = (ruleCode, subtypeCode, changes) => {
    setConfig((current) => updateRuleSubtype(current, ruleCode, subtypeCode, changes));
    setSaved(false);
  };

  const toggleRule = (rule, active) => {
    const needsSubtypes = [CRM_PET_RULES.NO_PRODUCT, CRM_PET_RULES.NO_ADDITIONAL].includes(rule.ruleCode);
    if (active && needsSubtypes && rule.subtypes.length === 0) {
      setError("Esta régua precisa ter subtipos configurados no backend antes de ser ativada.");
      return;
    }
    setError(null);
    updateRule(rule.ruleCode, { active });
  };

  const handleSave = async () => {
    if (!hasCompleteCrmPetMessages(config)) {
      setSaved(false);
      setError("Preencha as mensagens individual e consolidada de todas as réguas.");
      return;
    }

    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const result = await CCrmPet.saveConfig(prepareCrmPetConfigPayload(config));
      if (!result) {
        setError("Erro ao salvar as configurações.");
        return;
      }
      setConfig(clone(result));
      setSaved(true);
      toast.success("Configuração salva; reprocessamento agendado.", { theme: "colored" });
    } finally {
      setSaving(false);
    }
  };

  if (!showConfig) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="crm-pet-config-title">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <Settings size={18} className="text-scale-500" />
            <span id="crm-pet-config-title" className="font-semibold text-gray-800">Configurar réguas e mensagens</span>
          </div>
          <button type="button" onClick={CCrmPet.closeConfig} disabled={saving} aria-label="Fechar" className="border-none bg-transparent p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          <div className="rounded border border-scale-200 bg-scale-50 p-4 text-xs text-scale-700">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold"><Info size={15} /> Variáveis disponíveis</h2>
            <div className="flex flex-wrap gap-2">
              {VARIAVEIS.map((variable) => (
                <span key={variable.key} className="rounded border border-scale-200 bg-white px-2 py-1">
                  <code className="font-bold">{variable.key}</code> — {variable.desc}
                </span>
              ))}
            </div>
          </div>

          <section className="rounded border border-zinc-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-scale-500"><Settings size={15} /> Réguas da loja</h2>
            {loading ? <Loading /> : (
              <RulesTable
                rules={config.rules}
                saving={saving}
                onToggleRule={toggleRule}
                onUpdateSubtype={updateSubtype}
              />
            )}
          </section>

          <section className="rounded border border-zinc-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-scale-500"><MessageCircle size={15} /> Faixas antigas de ausência</h2>
            {loading ? <Loading /> : (
              <Table.Root name="crm-pet-config-ranges">
                <Table.Header columns={["Faixa:140px", "Mensagem WhatsApp:1fr"]} />
                <Table.Content>
                  {config.faixas.map((faixa, index) => (
                    <Table.Line key={`${faixa.label}-${index}`}>
                      <Table.Item><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${rangeClassName(faixa.dias)}`}>{faixa.label}</span></Table.Item>
                      <Table.Item>
                        <textarea
                          rows={3}
                          maxLength={4096}
                          value={faixa.mensagem || ""}
                          disabled={saving}
                          onChange={(event) => updateFaixa(index, event.target.value)}
                          className="w-full resize-y rounded border border-zinc-200 px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-scale-300"
                        />
                      </Table.Item>
                    </Table.Line>
                  ))}
                </Table.Content>
              </Table.Root>
            )}
          </section>

          {error && <p className="m-0 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>}
        </div>

        <div className="flex gap-3 border-t border-zinc-200 bg-gray-50 px-6 py-4">
          <Action.Root outline type="button" disabled={loading || saving} onClick={() => setConfig((current) => ({ ...current, faixas: clone(FAIXAS_DEFAULT) }))}>
            <Action.Icon icon={RotateCcw} size={16} /> Restaurar faixas
          </Action.Root>
          <div className="ml-auto flex gap-2">
            <Action.Root outline type="button" disabled={saving} onClick={CCrmPet.closeConfig}>Fechar</Action.Root>
            <Action.Root type="button" disabled={loading || saving} onClick={handleSave}>
              {saving ? <><RefreshCw size={14} className="animate-spin" /> Salvando...</> : saved ? <><Check size={14} /> Salvo</> : <><Save size={14} /> Salvar configurações</>}
            </Action.Root>
          </div>
        </div>
      </div>
    </div>
  );
}

function Loading() {
  return <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400"><RefreshCw size={15} className="animate-spin" /> Carregando...</div>;
}

export function RulesTable({ rules, saving, onToggleRule, onUpdateSubtype }) {
  return (
    <div className="overflow-x-auto">
      <div className="w-full">
        <Table.Root name="crm-pet-config-rules">
          <Table.Header columns={[
            "Ativa:50px",
            "Régua:170px",
            "Mensagem individual:minmax(300px, 1fr)",
            "Mensagem consolidada:minmax(300px, 1fr)"
          ]} />
          <Table.Content>
            {rules.filter((rule) => rule.ruleCode !== CRM_PET_RULES.ABSENT).map((rule) => {
              const subtype = rule.subtypes?.[0];

              return (
                <Table.Line key={rule.ruleCode}>
                  <Table.Item>
                    <label className="flex justify-center" title={rule.active ? "Desativar régua" : "Ativar régua"}>
                      <span className="sr-only">{rule.active ? "Desativar" : "Ativar"} {ruleLabel(rule.ruleCode)}</span>
                      <input
                        type="checkbox"
                        checked={rule.active}
                        disabled={saving}
                        onChange={(event) => onToggleRule(rule, event.target.checked)}
                        className="h-4 w-4 accent-scale-500"
                      />
                    </label>
                  </Table.Item>
                  <Table.Item>
                    <span className="font-semibold text-gray-700">{ruleLabel(rule.ruleCode)}</span>
                  </Table.Item>
                  <Table.Item>
                    {subtype ? (
                      <textarea
                        aria-label={`Mensagem individual de ${ruleLabel(rule.ruleCode)}`}
                        rows={5}
                        maxLength={4096}
                        required
                        value={subtype.individualTemplate}
                        disabled={saving}
                        onChange={(event) => onUpdateSubtype(rule.ruleCode, subtype.subtypeCode, { individualTemplate: event.target.value })}
                        className="m-2 w-[calc(100%-1rem)] resize-y rounded border border-zinc-200 bg-white p-2 text-xs text-gray-700 outline-none focus:ring-2 focus:ring-scale-300"
                      />
                    ) : (
                      <p className="m-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        Sem configuração de mensagem. As referências técnicas permanecem administradas pelo backend.
                      </p>
                    )}
                  </Table.Item>
                  <Table.Item>
                    {subtype ? (
                      <textarea
                        aria-label={`Mensagem consolidada de ${ruleLabel(rule.ruleCode)}`}
                        rows={5}
                        maxLength={4096}
                        required
                        value={subtype.consolidatedFragment}
                        disabled={saving}
                        onChange={(event) => onUpdateSubtype(rule.ruleCode, subtype.subtypeCode, { consolidatedFragment: event.target.value })}
                        className="m-2 w-[calc(100%-1rem)] resize-y rounded border border-zinc-200 bg-white p-2 text-xs text-gray-700 outline-none focus:ring-2 focus:ring-scale-300"
                      />
                    ) : (
                      <span className="px-3 text-xs text-gray-400">—</span>
                    )}
                  </Table.Item>
                </Table.Line>
              );
            })}
          </Table.Content>
        </Table.Root>
      </div>
    </div>
  );
}

function ruleLabel(ruleCode) {
  return {
    ABSENT: "Ausentes",
    NO_PRODUCT: "Sem produtos",
    NO_ADDITIONAL: "Sem adicionais",
    POTENTIAL_SUBSCRIBER: "Possíveis assinantes",
  }[ruleCode] || ruleCode;
}

function rangeClassName(days) {
  if (days == null) return "bg-gray-100 text-gray-600";
  if (days >= 45) return "bg-red-50 text-red-700";
  if (days >= 30) return "bg-orange-50 text-orange-700";
  if (days >= 15) return "bg-yellow-50 text-yellow-700";
  return "bg-blue-50 text-blue-700";
}
